import { useTranslation } from "react-i18next";

import Label from "@components/form/Label";
import Heading1 from "@components/layout/Heading";
import TrashIcon from "@components/icons/TrashIcon";
import StepFooter from "./StepFooter";
import { reportFilterInputValue, reportFilterStoredValue } from "./formatCell";
import { FilterOperator, ReportFilter } from "@services/reportEngine";
import { useAdhocReport } from "@context/AdhocReportContext";
import { useReportScopes } from "@hooks/query/useReportScopes";

export const OPERATOR_LABEL_KEYS: Record<FilterOperator, string> = {
  eq: "reporting.operator_eq",
  contains: "reporting.operator_contains",
  starts_with: "reporting.operator_starts_with",
  gt: "reporting.operator_gt",
  gte: "reporting.operator_gte",
  lt: "reporting.operator_lt",
  lte: "reporting.operator_lte",
};

// "equals 01/07/2026" and "greater than 01/07/2026" are not how anyone asks
// for a date, so dates say it the way a person would.
export const DATE_OPERATOR_LABEL_KEYS: Partial<Record<FilterOperator, string>> =
  {
    eq: "reporting.operator_date_eq",
    gte: "reporting.operator_date_gte",
    lte: "reporting.operator_date_lte",
  };

/**
 * Filters with real operators (CR §1.3.2): each field offers exactly the
 * operators the catalogue permits, values are typed inputs, filters are
 * added, edited and removed independently, and everything is ANDed.
 */
const StepFilters: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    filters,
    setFilters,
    selectedFields,
    selectedLevels = [],
    fieldByName,
    goToStep,
  } = useAdhocReport();
  const {
    data: schemes = [],
    isLoading: schemesLoading,
    isError: schemesError,
    refetch: refetchSchemes,
  } = useReportScopes();

  const schemeField = selectedLevels.includes("allocation_announcements")
    ? "allocationLevel1ResourceName"
    : "level1ResourceName";
  const filterableFields = Array.from(new Set([...selectedFields, schemeField]))
    .map(name => fieldByName.get(name))
    .filter((field): field is NonNullable<typeof field> =>
      Boolean(field && field.operators.length > 0),
    );

  const updateFilter = (index: number, patch: Partial<ReportFilter>) => {
    setFilters(
      filters.map((filter, i) => {
        if (i !== index) return filter;
        const next = { ...filter, ...patch };
        // A field change resets its value because text from the previous
        // field may be invalid or misleading for the new field's data type.
        // Keep the operator only when the new field supports it.
        if (patch.field) {
          const operators = fieldByName.get(patch.field)?.operators ?? [];
          next.value = "";
          if (!operators.includes(next.operator)) {
            next.operator = operators[0] ?? "eq";
          }
        }
        if (next.field === schemeField) next.operator = "eq";
        return next;
      }),
    );
  };

  const operatorLabelKey = (fieldName: string, operator: FilterOperator) =>
    (fieldByName.get(fieldName)?.dataType === "date" &&
      DATE_OPERATOR_LABEL_KEYS[operator]) ||
    OPERATOR_LABEL_KEYS[operator];

  const valueInputType = (fieldName: string) => {
    const dataType = fieldByName.get(fieldName)?.dataType;
    if (dataType === "date") return "date";
    if (
      dataType === "number" ||
      dataType === "volume" ||
      dataType === "percent"
    )
      return "number";
    return "text";
  };

  const addFilter = () => {
    const field = filterableFields[0];
    if (!field) return;
    setFilters([
      ...filters,
      {
        field: field.name,
        operator: field.operators[0] ?? "eq",
        value: "",
      },
    ]);
  };

  const setFiltersEnabled = (enabled: boolean) => {
    if (!enabled) {
      setFilters([]);
    } else if (filters.length === 0) {
      addFilter();
    }
  };

  return (
    <div className="flex flex-col grow">
      <div className="p-4 flex flex-col gap-4 grow">
        <div>
          <Heading1 light>{t("reporting.general_step3_title")}</Heading1>
          <div className="text-sm">{t("reporting.general_step3_subtitle")}</div>
        </div>

        {/* Keep the familiar legacy General Report interaction: first decide
            whether filtering is enabled, then edit compact expression rows.
            The operator remains explicit because the enhanced catalogue
            supports more than the legacy equals-only contract. */}
        <fieldset className="flex max-w-2xl flex-col gap-2">
          <Label>{t("reporting.filter_enabled")}</Label>
          <div className="flex gap-10">
            <label
              className="flex items-center gap-2"
              htmlFor="filters-enabled-yes"
            >
              <input
                id="filters-enabled-yes"
                name="filters-enabled"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={filters.length > 0}
                disabled={filterableFields.length === 0}
                onChange={event =>
                  event.target.checked && setFiltersEnabled(true)
                }
              />
              {t("common.yes")}
            </label>
            <label
              className="flex items-center gap-2"
              htmlFor="filters-enabled-no"
            >
              <input
                id="filters-enabled-no"
                name="filters-enabled"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={filters.length === 0}
                onChange={event =>
                  event.target.checked && setFiltersEnabled(false)
                }
              />
              {t("common.no")}
            </label>
          </div>
        </fieldset>

        {filters.length > 0 ? (
          <div className="flex w-full flex-col gap-2">
            {filters.map((filter, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="flex h-10 w-8 flex-none items-center justify-center text-3xl font-light text-primary-3"
                  aria-label={t("reporting.add_filter") as string}
                  onClick={addFilter}
                >
                  +
                </button>
                <select
                  aria-label={t("reporting.filter_field") as string}
                  className="min-w-[14rem] flex-[1_1_18rem] rounded border p-2"
                  value={filter.field}
                  onChange={e => updateFilter(index, { field: e.target.value })}
                >
                  {filterableFields.map(field => (
                    <option key={field.name} value={field.name}>
                      {t(field.label)}
                    </option>
                  ))}
                </select>
                {filter.field === schemeField ? (
                  <span className="min-w-[12rem] flex-[0_1_14rem] p-2">
                    {t(operatorLabelKey(filter.field, filter.operator))}
                  </span>
                ) : (
                  <select
                    aria-label={t("reporting.filter_operator") as string}
                    className="min-w-[12rem] flex-[0_1_14rem] rounded border p-2"
                    value={filter.operator}
                    onChange={e =>
                      updateFilter(index, {
                        operator: e.target.value as FilterOperator,
                      })
                    }
                  >
                    {(fieldByName.get(filter.field)?.operators ?? []).map(
                      operator => (
                        <option key={operator} value={operator}>
                          {t(operatorLabelKey(filter.field, operator))}
                        </option>
                      ),
                    )}
                  </select>
                )}
                <div className="relative min-w-[14rem] flex-[1_1_18rem]">
                  {filter.field === schemeField ? (
                    <select
                      aria-label={t("reporting.filter_value") as string}
                      className="w-full rounded border p-2"
                      value={filter.value}
                      disabled={schemesLoading || schemesError}
                      onChange={e =>
                        updateFilter(index, { value: e.target.value })
                      }
                    >
                      <option value="">
                        {t(schemesLoading ? "common.loading" : "common.select")}
                      </option>
                      {filter.value &&
                        !schemes.some(
                          scheme => scheme.name === filter.value,
                        ) && (
                          <option value={filter.value}>{filter.value}</option>
                        )}
                      {schemes.map(scheme => (
                        <option key={scheme.id} value={scheme.name}>
                          {scheme.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      aria-label={t("reporting.filter_value") as string}
                      className="w-full rounded border p-2 pr-12"
                      type={valueInputType(filter.field)}
                      step="any"
                      value={reportFilterInputValue(
                        filter.value,
                        fieldByName.get(filter.field)?.dataType,
                      )}
                      onChange={e =>
                        updateFilter(index, {
                          value: reportFilterStoredValue(
                            e.target.value,
                            fieldByName.get(filter.field)?.dataType,
                          ),
                        })
                      }
                    />
                  )}
                  {fieldByName.get(filter.field)?.dataType === "volume" && (
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
                      ML
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="flex h-10 w-10 flex-none items-center justify-center rounded hover:bg-gray-100 focus:outline-blue-500"
                  aria-label={t("common.delete") as string}
                  onClick={() =>
                    setFilters(filters.filter((_, i) => i !== index))
                  }
                >
                  <TrashIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <p className="text-xs text-gray-500">
          {t("reporting.filters_and_hint")}
        </p>
        {schemesError &&
          filters.some(filter => filter.field === schemeField) && (
            <div className="text-sm text-red-700">
              <p>{t("reporting.scope_load_failed")}</p>
              <button
                type="button"
                className="btn-default mt-2"
                onClick={() => refetchSchemes()}
              >
                {t("common.refresh")}
              </button>
            </div>
          )}
      </div>
      <StepFooter
        onBack={() => goToStep(0)}
        onContinue={() => goToStep(2)}
        continueDisabled={filters.some(
          filter => `${filter.value}`.trim() === "",
        )}
      />
    </div>
  );
};

export default StepFilters;
